/**
 * particulas.ts — Canvas leve com particulas neon + linhas conectando
 * (efeito "rede neural") no fundo do hub.
 *
 * Caracteristicas:
 * - RAF unico; pausa quando aba esta oculta (Page Visibility)
 * - Respeita prefers-reduced-motion (nao inicia animacao)
 * - Densidade adaptativa: numero de particulas proporcional ao viewport
 *   (cap em mobile pra nao travar)
 * - DPR-aware (nitido em telas retina sem custo extra)
 */

interface Particula {
  x: number;
  y: number;
  vx: number;
  vy: number;
  raio: number;
  cor: string;
}

const CORES = [
  'rgba(30, 136, 229, 1)',    // azul da marca
  'rgba(18, 184, 162, 1)',    // teal da marca
  'rgba(94, 234, 212, 0.9)',  // mint (teal claro)
  'rgba(253, 196, 0, 0.75)',  // amarelo da marca
];

const DIST_CONEXAO = 130;      // distancia maxima pra desenhar linha entre 2 particulas
const VEL_MAX      = 0.25;     // velocidade base
const RAIO_MIN     = 0.8;
const RAIO_MAX     = 2.2;

export function iniciarParticulas(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return;

  // Respeita reduce-motion: desenha um quadro estatico e para
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let particulas: Particula[] = [];
  let largura = 0;
  let altura = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  let rafId = 0;
  let pausado = false;

  function calcularQuantidade(): number {
    // Densidade: ~1 particula a cada 12000px2, com floor pra mobile.
    const area = window.innerWidth * window.innerHeight;
    const base = Math.round(area / 12000);
    if (window.innerWidth < 600) return Math.min(base, 35);
    if (window.innerWidth < 1024) return Math.min(base, 60);
    return Math.min(base, 90);
  }

  function rand(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  function criarParticulas(qtd: number): void {
    particulas = [];
    for (let i = 0; i < qtd; i++) {
      particulas.push({
        x:    Math.random() * largura,
        y:    Math.random() * altura,
        vx:   rand(-VEL_MAX, VEL_MAX),
        vy:   rand(-VEL_MAX, VEL_MAX),
        raio: rand(RAIO_MIN, RAIO_MAX),
        cor:  CORES[Math.floor(Math.random() * CORES.length)]!,
      });
    }
  }

  function redimensionar(): void {
    largura = window.innerWidth;
    altura  = window.innerHeight;
    dpr     = Math.min(window.devicePixelRatio || 1, 2);

    canvas.width  = Math.floor(largura * dpr);
    canvas.height = Math.floor(altura  * dpr);
    canvas.style.width  = `${largura}px`;
    canvas.style.height = `${altura}px`;
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

    criarParticulas(calcularQuantidade());
  }

  function tick(): void {
    if (pausado) { rafId = requestAnimationFrame(tick); return; }

    ctx!.clearRect(0, 0, largura, altura);

    // 1. Move particulas
    for (const p of particulas) {
      p.x += p.vx;
      p.y += p.vy;

      // Wrap nas bordas
      if (p.x < -10)        p.x = largura + 10;
      if (p.x > largura+10) p.x = -10;
      if (p.y < -10)        p.y = altura + 10;
      if (p.y > altura +10) p.y = -10;
    }

    // 2. Desenha linhas (loop O(n2), mas com early-exit por distancia)
    //    Com cap de ~90 particulas isso e ~4000 testes por frame: barato.
    ctx!.lineWidth = 1;
    for (let i = 0; i < particulas.length; i++) {
      const a = particulas[i]!;
      for (let j = i + 1; j < particulas.length; j++) {
        const b = particulas[j]!;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist2 = dx*dx + dy*dy;
        const dmax2 = DIST_CONEXAO * DIST_CONEXAO;
        if (dist2 > dmax2) continue;

        const opacidade = (1 - Math.sqrt(dist2) / DIST_CONEXAO) * 0.35;
        ctx!.strokeStyle = `rgba(94, 234, 212, ${opacidade.toFixed(3)})`;
        ctx!.beginPath();
        ctx!.moveTo(a.x, a.y);
        ctx!.lineTo(b.x, b.y);
        ctx!.stroke();
      }
    }

    // 3. Desenha particulas (com pequeno glow via shadow)
    ctx!.shadowBlur = 8;
    for (const p of particulas) {
      ctx!.fillStyle   = p.cor;
      ctx!.shadowColor = p.cor;
      ctx!.beginPath();
      ctx!.arc(p.x, p.y, p.raio, 0, Math.PI * 2);
      ctx!.fill();
    }
    ctx!.shadowBlur = 0;

    rafId = requestAnimationFrame(tick);
  }

  function quadroEstatico(): void {
    ctx!.clearRect(0, 0, largura, altura);
    // So pontos, sem linhas, sem glow — barato e sem animacao
    for (const p of particulas) {
      ctx!.fillStyle = p.cor;
      ctx!.beginPath();
      ctx!.arc(p.x, p.y, p.raio, 0, Math.PI * 2);
      ctx!.fill();
    }
  }

  // ---- Inicializacao + listeners ----
  redimensionar();

  if (reduceMotion) {
    quadroEstatico();
    return;
  }

  rafId = requestAnimationFrame(tick);

  // Resize com debounce
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(redimensionar, 150);
  });

  // Pausa quando aba esta oculta (economiza CPU/bateria)
  document.addEventListener('visibilitychange', () => {
    pausado = document.hidden;
  });

  // Cleanup defensivo (se alguem decidir importar e re-iniciar)
  window.addEventListener('beforeunload', () => {
    cancelAnimationFrame(rafId);
  });
}
